import type { SetStateAction } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoginModalType, PostAuthOptions } from 'types';
import type { Image } from 'types/capi/shared';

import ModalNav from '~/components/Modals/ModalNav';
import useUser from '~/hooks/useUser';
import { isAppleMobileDevice } from '~/utils/browser';

import AuthDialog from '../AuthDialog';
import DownloadAppFooter from '../DownloadAppFooter';
import ErrorBanner from '../ErrorBanner';
import ForgotPasswordScreen from './ForgotPasswordScreen';
import SignInScreen from './SignInScreen';

export enum Stage {
  Error = -1,
  Initial = 0,
  ForgotPass = 1
}

type Props = {
  hideAppDownloadPrompt?: boolean;
  image?: Image;
  modalType: LoginModalType;
  onBackNavigation?: () => void;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  postAuthOptions?: PostAuthOptions;
};

export default function EmailLogin({
  onDismiss,
  postAuthOptions,
  modalType,
  onBackNavigation,
  hideAppDownloadPrompt = false
}: Props) {
  const [stage, setStage] = useState(Stage.Initial);
  const [error, setError] = useState<string | null>(null);
  const user = useUser();
  const { t } = useTranslation('common');

  if (user.isLoggedIn) return null;

  const showAppDownload =
    isAppleMobileDevice(navigator.userAgent) &&
    !hideAppDownloadPrompt &&
    stage === Stage.Initial &&
    !['account', 'purchase', 'profile'].includes(modalType);

  function onBackNavigationOverride() {
    if (stage === Stage.Initial && onBackNavigation) return onBackNavigation();

    setError(null);
    setStage(stage - 1);
  }

  return (
    <AuthDialog onDismiss={() => onDismiss()}>
      <ModalNav
        heading={
          stage === Stage.ForgotPass
            ? t('login.resetPassword')
            : t('login.stageSignIn.title')
        }
        onBack={onBackNavigation ? onBackNavigationOverride : undefined}
        onDismissHref={modalType === 'profile' ? '/' : undefined}
      />

      <Screen
        onDismiss={onDismiss}
        stage={stage}
        postAuthOptions={postAuthOptions}
        onError={setError}
        setStage={setStage}
      />

      {error && <ErrorBanner error={error} />}
      {showAppDownload && <DownloadAppFooter modalType={modalType} />}
    </AuthDialog>
  );
}

export type ScreenProps = {
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  onError: (err: string | null) => void;
  postAuthOptions?: PostAuthOptions;
  setStage: React.Dispatch<SetStateAction<Stage>>;
  stage: Stage;
};

function Screen(props: ScreenProps) {
  switch (props.stage) {
    case Stage.Initial:
      return <SignInScreen {...props} />;
    case Stage.ForgotPass:
      return <ForgotPasswordScreen />;
  }
}
